import { ITranslationFactors } from 'legacy/lut/classes/models/partner360/ITranslationFactors'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IJBPPlanningItem } from '../../models/IJBPPlanning'
import { TSType } from '../models'
import { IFieldProps } from '../models/IFieldProps'

import { getFieldName } from './getFieldName'
import { setItemValues } from './setItemValues'

type TActualsType = Exclude<ActualsValueTypes, ActualsValueTypes.None>

const actualsTypes = Object.values(ActualsValueTypes).filter(
  (type): type is TActualsType => type !== ActualsValueTypes.None
)
const legacyTypes = new Set<TActualsType>([ActualsValueTypes.Net, ActualsValueTypes.Ndp])

const getActualsFactor = (
  factors: ITranslationFactors,
  sourceType: TActualsType,
  targetType: TActualsType
): number | undefined => {
  if (sourceType === targetType) return 1

  // Keep the original direction: Ndp -> Net multiplies by NetToNdp.
  const factor = factors[`${targetType}To${sourceType}`]
  if (legacyTypes.has(sourceType) && legacyTypes.has(targetType)) {
    return factor || 1
  }

  // Extra AVTs require an effective coefficient; *Calc fields are informational.
  return typeof factor === 'number' && Number.isFinite(factor) ? factor || 1 : undefined
}

const getUnitsTypes = (factors: ITranslationFactors, sourceType: TActualsType): TActualsType[] => {
  const configuredTypes = new Set(
    typeof factors.ActualsValueTypes === 'string'
      ? factors.ActualsValueTypes.split(',').map((type) => type.trim())
      : []
  )

  return actualsTypes.filter(
    (type) =>
      type === sourceType ||
      legacyTypes.has(type) ||
      configuredTypes.has(type) ||
      actualsTypes.some(
        (otherType) =>
          typeof factors[`${type}To${otherType}`] === 'number' ||
          typeof factors[`${otherType}To${type}`] === 'number'
      )
  )
}

export const setItemValuesUniversal = (
  item: IJBPPlanningItem,
  value: string | number | null,
  fieldProps: IFieldProps,
  category: string,
  isManual: boolean,
  translationFactors?: ITranslationFactors[] | null,
  sTypeOriginal?: TSType
): IJBPPlanningItem => {
  // Reuse every original edit/rounding rule, without its Net/Ndp conversion branch.
  const newItem = setItemValues(
    item,
    value,
    fieldProps,
    category,
    isManual,
    undefined,
    sTypeOriginal
  )
  const factors = translationFactors?.find((factor) => factor.Category === category)
  if (fieldProps.isSoB || !factors) return newItem

  const numValue = newItem[getFieldName(fieldProps)] as number | null
  const sourceType = fieldProps.actualsType || ActualsValueTypes.Net

  if (fieldProps.isUnits) {
    // Units are identical across AVTs, including configured types without price factors.
    for (const actualsType of getUnitsTypes(factors, sourceType)) {
      newItem[getFieldName({ ...fieldProps, actualsType })] = numValue
    }
    return newItem
  }

  const sourceCurrency = !fieldProps.currency || fieldProps.currency === 'USD' ? 'USD' : 'LC'

  for (const actualsType of actualsTypes) {
    const actualsFactor = getActualsFactor(factors, sourceType, actualsType)
    if (actualsFactor === undefined) continue

    for (const currency of ['USD', 'LC'] as const) {
      // Keep the original currency direction and fallback for missing/zero rates.
      const currencyFactor =
        currency === sourceCurrency
          ? 1
          : currency === 'LC'
            ? factors.LCToUsd || 1
            : factors.UsdToLC || 1
      const fieldName = getFieldName({ ...fieldProps, actualsType, currency })
      const isSourceField = actualsType === sourceType && currency === sourceCurrency

      // Round only the final product, with currency multiplication first as in the original.
      newItem[fieldName] = isSourceField
        ? numValue
        : numValue || numValue === 0
          ? Number((numValue * currencyFactor * actualsFactor).toFixed(0))
          : null
    }
  }

  return newItem
}
